import {
  GrabIconButton,
  Stack,
  Typography,
  DeleteIconButton,
  Box,
  DELETE_WARNING,
  Checkbox,
  RichTextEditor,
  RichTextReader,
} from "@compass/core";
import { ApplicationAnswer, ApplicationQuestion, ApplicationViewResource, FormAnswer } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import { Input, Select } from "components";
import { Draggable } from "react-beautiful-dnd";

type ApplicationQuestionAnswerProps = {
  answer: ApplicationAnswer;
  index: number;
  updateAnswer: (value: ApplicationAnswer) => void;
  branchableQuestions: ApplicationQuestion[];
  selected: boolean | undefined;
  isBranchable: boolean;
  mappableEntities?: any[];
  viewResource: ApplicationViewResource;
};

export const ApplicationQuestionAnswer = ({
  answer,
  index,
  updateAnswer,
  branchableQuestions,
  isBranchable,
  mappableEntities,
  selected,
  viewResource,
}: ApplicationQuestionAnswerProps) => {
  const [formAnswer, setFormAnswer] = useState<ApplicationAnswer | undefined>(answer);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  useEffect(() => {
    setFormAnswer(answer);
  }, [answer]);

  useEffect(() => {
    updateAnswer(formAnswer!);
  }, [formAnswer]);

  const grid = 8;

  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    padding: grid * 1,
    margin: `0 0 ${grid}px 0`,

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const deleteAnswer = (e) => {
    if (window.confirm(DELETE_WARNING)) {
      e.stopPropagation();
      setFormAnswer(
        produce((draft) => {
          draft!.deleted = true;
        })
      );
    }
  };

  const otherWriteInTypeField = () => {
    return (
      <Stack direction="row" style={{ minWidth: 200 }}>
        <Select
          required
          label="Other Write-In Type"
          value={viewResource?.formQuestionWriteInTypes?.find((x) => x.id === formAnswer?.otherWriteInTypeId)}
          options={viewResource?.formQuestionWriteInTypes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            setFormAnswer(
              produce((draft) => {
                draft!.otherWriteInTypeId = value?.id;
              })
            )
          }
        />
      </Stack>
    );
  };

  const entityField = () => {
    return (
      <Box style={{ minWidth: 240 }}>
        <Select
          label="Map To"
          value={mappableEntities?.find((x) => x.id == formAnswer?.entityId)}
          options={mappableEntities}
          getOptionLabel={(option) => option.name || "No Label"}
          onChange={(e, value) =>
            setFormAnswer(
              produce((draft) => {
                draft!.entityId = value?.id;
              })
            )
          }
        />
      </Box>
    );
  };

  const branchQuestionField = () => {
    return (
      <Box style={{ minWidth: 180 }}>
        <Select
          label="Branch Question"
          value={branchableQuestions?.find((x) => x.tempId == formAnswer?.branchToApplicationQuestionTempId)}
          options={branchableQuestions}
          getOptionLabel={(option) => option.label || "No Label"}
          onChange={(e, value) =>
            setFormAnswer(
              produce((draft) => {
                draft!.branchToApplicationQuestionTempId = value?.tempId;
              })
            )
          }
        />
      </Box>
    );
  };

  const isOtherField = () => {
    return (
      <Checkbox
        label="Is Other"
        checked={formAnswer?.isOther}
        onChange={(e) =>
          setFormAnswer(
            produce((draft) => {
              draft!.isOther = e.target.checked;
            })
          )
        }
      />
    );
  };

  const isEndAnswerField = () => {
    return (
      <Checkbox
        label="Is End Answer"
        checked={formAnswer?.isEndAnswer}
        onChange={(e) =>
          setFormAnswer(
            produce((draft) => {
              draft!.isEndAnswer = e.target.checked;
            })
          )
        }
      />
    );
  };

  return (
    <Draggable
      key={`${formAnswer?.id}:${formAnswer?.displayOrder}`}
      draggableId={`${formAnswer?.id}:${formAnswer?.displayOrder}`}
      index={index || 0}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
        >
          <Box onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <Stack direction="row" spacing="sm" alignItems="center">
              <Box style={{ minWidth: 300 }}>
                {!selected && <RichTextReader html={formAnswer?.name} />}
                {selected && (
                  <RichTextEditor
                    justStyle
                    placeholder="Add New Answer"
                    value={formAnswer?.name}
                    onChange={(value) =>
                      setFormAnswer(
                        produce((draft) => {
                          draft!.name = value!;
                        })
                      )
                    }
                  />
                )}
              </Box>

              {mappableEntities && mappableEntities.length > 0 && selected && (
                <>{entityField()}</>
              )}

              {isBranchable && selected && (
                <>{branchQuestionField()}</>
              )}

              {selected && !isHovered! && formAnswer?.isOther && <>{otherWriteInTypeField()}</>}

              <Box {...provided.dragHandleProps} style={{ width: "100%" }}>
                {selected && isHovered && answer && (
                  <Stack direction="column">
                    <Stack direction="row">
                      {isOtherField()}

                      {formAnswer?.isOther && (
                        <Stack direction="row" spacing="md" style={{ marginRight: "5px" }}>
                          {otherWriteInTypeField()}
                        </Stack>
                      )}

                      {isEndAnswerField()}

                      <GrabIconButton {...provided.dragHandleProps} />
                      <DeleteIconButton
                        onClick={(e) => {
                          deleteAnswer(e);
                        }}
                      />
                    </Stack>

                    {formAnswer?.isEndAnswer && (
                      <RichTextEditor
                        label="End Message"
                        placeholder="End Message..."
                        value={formAnswer.endAnswerText}
                        onChange={(value) =>
                          setFormAnswer(
                            produce((draft) => {
                              draft!.endAnswerText = value!;
                            })
                          )
                        }
                      />
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        </div>
      )}
    </Draggable>
  );
};
